import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Lock, Eye, Database, Globe, UserCheck, Mail, Cookie, Trash2 } from 'lucide-react';

const Privacy = () => {
  const lastUpdated = "January 27, 2026";
  
  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">
            Last updated: {lastUpdated}
          </p>
        </motion.div>

        <Card className="bg-card border-border mb-8">
          <CardContent className="p-6 md:p-8">
            <div className="prose prose-invert max-w-none">
              
              {/* Introduction */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Eye className="w-5 h-5" />
                  1. Introduction
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong className="text-foreground">Knowledge Based Trading Ltd</strong> (Company No. 17040814), trading as 
                  <strong className="text-foreground"> Traders Capital</strong> ("Company," "we," "us," or "our"), 
                  is committed to protecting your privacy and personal data. Our registered office is at 
                  167-169 Great Portland Street, London, England, W1W 5PF.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                  when you use our evaluation-based trading platform. Please read this policy carefully to 
                  understand our practices regarding your personal data.
                </p>
              </section>

              {/* GDPR Compliance */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5" />
                  2. GDPR Compliance
                </h2>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                  <p className="text-green-400 font-medium mb-2">DATA PROTECTION</p>
                  <p className="text-muted-foreground text-sm">
                    We comply with the General Data Protection Regulation (GDPR) and other applicable data 
                    protection laws. You have rights over your personal data, and we are committed to 
                    helping you exercise those rights.
                  </p>
                </div>
              </section>

              {/* Information We Collect */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Database className="w-5 h-5" />
                  3. Information We Collect
                </h2>
                
                <h3 className="font-bold text-foreground mt-6 mb-3">3.1 Information You Provide</h3>
                <ul className="space-y-2 text-muted-foreground mb-4">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Account Information:</strong> Name, email address, phone number, country of residence</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Identity Verification (KYC):</strong> Government-issued ID, proof of address, selfie verification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Payment Information:</strong> Billing details, transaction history (processed securely via Stripe)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Banking Details:</strong> Bank account information for payout processing</span>
                  </li>
                </ul>

                <h3 className="font-bold text-foreground mt-6 mb-3">3.2 Information Collected Automatically</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Device Information:</strong> IP address, browser type, operating system</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Usage Data:</strong> Pages visited, features used, trading activity on our platform</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Cookies:</strong> Session cookies for authentication and preferences</span>
                  </li>
                </ul>
              </section>

              {/* How We Use Your Information */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5" />
                  4. How We Use Your Information
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use your personal data for the following purposes:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>To create and manage your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>To process payments and payouts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>To verify your identity (KYC compliance)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>To provide customer support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>To send important account notifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>To improve our platform and services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>To comply with legal obligations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>To prevent fraud and ensure platform security</span>
                  </li>
                </ul>
              </section>

              {/* Data Sharing */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5" />
                  5. Data Sharing
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We do <strong className="text-foreground">NOT</strong> sell your personal data. We may share 
                  your information with:
                </p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Payment Processors:</strong> Stripe for secure payment processing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Identity Verification Services:</strong> For KYC compliance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Legal Authorities:</strong> When required by law or to protect our rights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span><strong className="text-foreground">Service Providers:</strong> Who assist in operating our platform (under strict confidentiality agreements)</span>
                  </li>
                </ul>
              </section>

              {/* Data Security */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5" />
                  6. Data Security
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We implement robust security measures to protect your data:
                </p>
                <div className="grid gap-3">
                  <div className="p-3 bg-secondary/30 rounded-lg flex items-center gap-3">
                    <Lock className="w-5 h-5 text-green-500 shrink-0" />
                    <span className="text-muted-foreground text-sm">SSL/TLS encryption for all data transmission</span>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg flex items-center gap-3">
                    <Shield className="w-5 h-5 text-green-500 shrink-0" />
                    <span className="text-muted-foreground text-sm">Encrypted storage for sensitive documents (KYC)</span>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg flex items-center gap-3">
                    <UserCheck className="w-5 h-5 text-green-500 shrink-0" />
                    <span className="text-muted-foreground text-sm">Restricted access to personal data (need-to-know basis)</span>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg flex items-center gap-3">
                    <Database className="w-5 h-5 text-green-500 shrink-0" />
                    <span className="text-muted-foreground text-sm">Regular security audits and monitoring</span>
                  </div>
                </div>
              </section>

              {/* Data Retention */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Database className="w-5 h-5" />
                  7. Data Retention
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We retain your personal data for as long as necessary to:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Provide our services to you</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Comply with legal and regulatory requirements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Resolve disputes and enforce our agreements</span>
                  </li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  KYC documents are retained for a minimum of 5 years after account closure, as required 
                  by anti-money laundering regulations.
                </p>
              </section>

              {/* Your Rights */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <UserCheck className="w-5 h-5" />
                  8. Your Rights (GDPR)
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Under GDPR, you have the following rights:
                </p>
                <div className="grid gap-3">
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <h4 className="font-bold text-foreground mb-1">Right to Access</h4>
                    <p className="text-muted-foreground text-sm">Request a copy of your personal data</p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <h4 className="font-bold text-foreground mb-1">Right to Rectification</h4>
                    <p className="text-muted-foreground text-sm">Request correction of inaccurate data</p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <h4 className="font-bold text-foreground mb-1">Right to Erasure</h4>
                    <p className="text-muted-foreground text-sm">Request deletion of your data (subject to legal requirements)</p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <h4 className="font-bold text-foreground mb-1">Right to Portability</h4>
                    <p className="text-muted-foreground text-sm">Receive your data in a machine-readable format</p>
                  </div>
                  <div className="p-4 bg-secondary/30 rounded-lg">
                    <h4 className="font-bold text-foreground mb-1">Right to Object</h4>
                    <p className="text-muted-foreground text-sm">Object to processing of your data for marketing purposes</p>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  To exercise any of these rights, please contact us at the email address below.
                </p>
              </section>

              {/* Cookies */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Cookie className="w-5 h-5" />
                  9. Cookies
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use cookies to:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Keep you logged in to your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Remember your preferences</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Analyze platform usage to improve our services</span>
                  </li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  You can control cookies through your browser settings. However, disabling certain cookies 
                  may affect your ability to use our platform.
                </p>
              </section>

              {/* Children's Privacy */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5" />
                  10. Children's Privacy
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our services are not intended for individuals under 18 years of age. We do not knowingly 
                  collect personal data from children. If we become aware that we have collected data from 
                  a child, we will take steps to delete it promptly.
                </p>
              </section>

              {/* Changes to Policy */}
              <section className="mb-8">
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Trash2 className="w-5 h-5" />
                  11. Changes to This Policy
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any significant 
                  changes by posting the new policy on this page and updating the "Last updated" date. 
                  We encourage you to review this policy periodically.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="font-heading text-xl font-bold text-primary flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5" />
                  12. Contact Us
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or wish to exercise your data rights, 
                  please contact us:
                </p>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-foreground font-medium">Knowledge Based Trading Ltd</p>
                  <p className="text-muted-foreground text-sm">Trading as: Traders Capital</p>
                  <p className="text-muted-foreground text-sm">Company No: 17040814</p>
                  <p className="text-muted-foreground text-sm mt-2">Registered Office: 167-169 Great Portland Street, London, England, W1W 5PF</p>
                  <p className="text-muted-foreground text-sm mt-2">Data Protection Email: privacy@traderscapital.net</p>
                  <p className="text-muted-foreground text-sm">General Support: support@traderscapital.net</p>
                </div>
              </section>

            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;
